import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export type LexgosignStatus =
  | 'CREATED'
  | 'SENT'
  | 'IN_PROGRESS'
  | 'SUCCESS'
  | 'VOIDED'
  | 'ERROR';

export interface LexgosignAlertRecipient {
  id: number;
  name?: string | null;
  email?: string | null;
}

export interface LexgosignAlert {
  id: number;
  envelopeId: number;
  envelopeName?: string | null;
  attachmentName?: string | null;
  subject: string;
  body: string;
  alertDate: string;
  alertTime?: string;
  sent: boolean;
  sentAt?: string | null;
  createdBy?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  canManage?: boolean;
  recipients: LexgosignAlertRecipient[];
}

export interface LexgosignRecipient {
  id?: number;
  recipientId?: string;
  name: string;
  email: string;
  status: 'PENDING' | 'SIGNED' | 'REJECTED' | 'IN_PROGRESS' | string;
  lexgoStatus?: string;
  signingUrl?: string | null;
  updatedAt?: string | null;
}

export interface LexgosignEnvelope {
  id: string;
  envelopeId?: string;
  requirementId?: number | string | null;
  name: string;
  attachmentName: string;
  attachmentId?: number | null;
  signedAttachmentId?: number;
  evidenceAttachmentId?: number;
  evidenceUrl?: string;
  documentUrl?: string;
  pageCount?: number;
  status: LexgosignStatus | string;
  lexgoStatus?: string;
  recipients: LexgosignRecipient[];
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
  signedCount?: number;
  totalRecipients?: number;
}

export interface LexgosignPaymentConfig {
  enabled: boolean;
  mock: boolean;
  currency: string;
  advancedUnitPriceUsd: number;
  vatRate: number;
  stripePublishableKey: string;
  stripeConfigured: boolean;
  manualBillingEnabled: boolean;
}

export interface LexgosignPaymentQuote {
  currency: string;
  documentCount: number;
  unitPriceUsd: number;
  subtotalUsd: number;
  discountUsd: number;
  vatRate: number;
  vatUsd: number;
  totalUsd: number;
  amountCents: number;
  label: string;
  coupon?: { code: string; percentOff?: number; amountOff?: number } | null;
  couponValid?: boolean | null;
  couponInvalid?: boolean;
}

export interface LexgosignPaymentIntentResult {
  mock: boolean;
  paymentIntentId: string;
  clientSecret: string;
  quote: LexgosignPaymentQuote;
}

export interface LexgosignTemplateField {
  id?: number;
  key: string;
  label: string;
  type: 'text' | 'date' | 'number' | 'textarea' | string;
  sourceText?: string;
  required?: boolean;
  sortOrder?: number;
}

export interface LexgosignTemplateCategory {
  id: number;
  name: string;
  sortOrder?: number;
  parentId?: number | null;
  templateCount?: number;
  createdAt?: string;
  children?: LexgosignTemplateCategory[];
}

export interface LexgosignTemplate {
  id: number;
  categoryId: number;
  categoryName?: string | null;
  parentId?: number | null;
  parentName?: string | null;
  name: string;
  originalFilename?: string;
  createdBy?: number | null;
  createdByName?: string | null;
  createdAt?: string;
  fields: LexgosignTemplateField[];
  documentText?: string;
}

export interface LexgosignTemplateAnalyzeResult {
  textPreview?: string;
  documentText?: string;
  fields: LexgosignTemplateField[];
  warning?: string | null;
}

export interface LexgosignTemplateGenerateResult {
  name: string;
  filename: string;
  pdfBase64: string;
  archive?: {
    key?: string;
    path?: string;
    filename?: string;
    url?: string | null;
  };
}

export interface LexgosignSettings {
  branding?: {
    logo?: string;
    url_homepage?: string;
    url_signature_flow?: string;
    name?: string;
    reply_to?: string;
    sender_email?: string;
  };
  documents?: {
    document_id_enabled?: boolean | string;
    custom_page_id?: string;
  };
  emails?: {
    envelope_invitation?: boolean | string;
    envelope_voided?: boolean | string;
    envelope_rejected?: boolean | string;
    envelope_signed?: boolean | string;
  };
  localization?: {
    es?: { extra_page_intro?: string; emails?: any };
    en?: { extra_page_intro?: string; emails?: any };
  };
  signature?: {
    type?: string;
    style?: string;
    bg_image?: string;
  };
  signature_flow?: {
    language?: string;
    sender_name?: string;
    signature_order?: string;
    signature_types?: string;
    signature_color?: string;
  };
  validations?: {
    email?: {
      enabled?: boolean | string;
      order?: number;
    };
  };
}

@Injectable({
  providedIn: 'root'
})
export class LexgosignService {
  private apiUrl = `${environment.apiUrl}/trackvision/lexgosign`;

  constructor(private http: HttpClient) {}

  private authHeaders(): HttpHeaders {
    return new HttpHeaders().set(
      'Token',
      localStorage.getItem('access_token') || ''
    );
  }

  private unwrap(resp: any) {
    return resp?.result?.result ?? resp?.result ?? resp;
  }

  /** Respuestas { success, result/message } envueltas por routeReader */
  private unwrapAction(resp: any) {
    if (resp?.result && typeof resp.result === 'object' && 'success' in resp.result) {
      return resp.result;
    }
    return this.unwrap(resp);
  }

  /** Lee public.configs → domains.<host>.lexgosign.enabled (sin exponer secretos). */
  isEnabled(): Observable<boolean> {
    return this.http
      .get<any>(`${this.apiUrl}/status`, { headers: this.authHeaders() })
      .pipe(
        map((resp) => {
          const raw = this.unwrap(resp);
          return !!(raw && raw.enabled);
        }),
        catchError(() => of(false))
      );
  }

  listByRequirement(requirementId: number | string): Observable<LexgosignEnvelope[]> {
    return this.http
      .get<any>(`${this.apiUrl}/${requirementId}`, { headers: this.authHeaders() })
      .pipe(
        map((resp) => {
          const raw = this.unwrap(resp);
          return Array.isArray(raw) ? raw : [];
        })
      );
  }

  /** Bandeja personal: solo pendientes de firma del usuario logueado */
  listMine(): Observable<LexgosignEnvelope[]> {
    return this.http
      .get<any>(`${this.apiUrl}/mine`, { headers: this.authHeaders() })
      .pipe(
        map((resp) => {
          const raw = this.unwrap(resp);
          return Array.isArray(raw) ? raw : [];
        })
      );
  }

  /** Listado admin: todos los envelopes del dominio */
  listAll(params?: { status?: string; q?: string; limit?: number; offset?: number }): Observable<LexgosignEnvelope[]> {
    const query: any = {};
    if (params?.status) query.status = params.status;
    if (params?.q) query.q = params.q;
    if (params?.limit) query.limit = params.limit;
    if (params?.offset != null) query.offset = params.offset;
    return this.http
      .get<any>(this.apiUrl, { headers: this.authHeaders(), params: query })
      .pipe(
        map((resp) => {
          const raw = this.unwrap(resp);
          return Array.isArray(raw) ? raw : [];
        })
      );
  }

  create(payload: {
    requirementId?: number | string | null;
    name: string;
    attachmentName: string;
    attachmentId?: number | null;
    documentBase64?: string;
    documentName?: string;
    recipients: {
      name: string;
      email: string;
      identification?: string;
      phone?: string;
      repId?: string;
      lastname?: string;
    }[];
    createdBy?: string;
    sequential?: boolean;
    useExtraPage?: boolean;
    signatureStyle?: 'SIMPLE' | 'FULL';
    electronicSignatureType?: 'SIMPLE' | 'ADVANCED';
    paymentMethod?: 'card' | 'manual';
    paymentIntentId?: string;
    couponCode?: string;
    documentCount?: number;
    signerMessage?: string;
    language?: 'ES' | 'EN' | string;
    archivePath?: string;
    saveEvidence?: boolean;
    placements?: {
      recipientIndex: number;
      page: number;
      top: number;
      left: number;
    }[];
  }): Observable<any> {
    const body: any = {
      name: payload.name,
      createdBy: payload.createdBy || '',
      sequential: !!payload.sequential,
      useExtraPage: payload.useExtraPage !== false,
      signingparties: (payload.recipients || []).map((r) => {
        const first = (r.name || '').trim();
        const last = (r.lastname || '').trim();
        let name = first;
        let lastname = last;
        if (!lastname && first.includes(' ')) {
          const parts = first.split(/\s+/);
          name = parts.shift() || first;
          lastname = parts.join(' ');
        }
        return {
          name,
          lastname,
          email: r.email,
          identification: r.identification || null,
          phone: r.phone || null,
          repId: r.repId || null
        };
      })
    };

    if (payload.signatureStyle === 'SIMPLE' || payload.signatureStyle === 'FULL') {
      body.signatureStyle = payload.signatureStyle;
    }
    if (payload.electronicSignatureType === 'SIMPLE' || payload.electronicSignatureType === 'ADVANCED') {
      body.electronicSignatureType = payload.electronicSignatureType;
    }
    if (payload.electronicSignatureType === 'ADVANCED') {
      body.paymentMethod = payload.paymentMethod || 'card';
      body.documentCount = payload.documentCount != null ? payload.documentCount : 1;
      if (payload.couponCode) {
        body.couponCode = String(payload.couponCode).trim();
      }
      if (payload.paymentMethod === 'card' && payload.paymentIntentId) {
        body.paymentIntentId = payload.paymentIntentId;
      }
    }
    const msg = (payload.signerMessage || '').trim();
    if (msg) {
      body.signerMessage = msg.slice(0, 500);
    }
    if (payload.language) {
      body.language = String(payload.language).toUpperCase() === 'EN' ? 'EN' : 'ES';
    }
    if (payload.archivePath) {
      body.archivePath = String(payload.archivePath).trim() || 'Contratos';
    }
    if (payload.saveEvidence != null) {
      body.saveEvidence = !!payload.saveEvidence;
    }

    if (payload.attachmentId != null) {
      body.attachmentId = Number(payload.attachmentId);
      body.reference = Number(payload.requirementId);
    } else if (payload.documentBase64) {
      body.documentBase64 = payload.documentBase64;
      body.documentName = payload.documentName || payload.attachmentName || 'document.pdf';
      if (payload.requirementId != null && payload.requirementId !== '') {
        body.reference = Number(payload.requirementId);
      }
    }

    if (Array.isArray(payload.placements) && payload.placements.length) {
      body.placements = payload.placements;
      body.useExtraPage = false;
    }

    return this.http.post<any>(this.apiUrl, body, { headers: this.authHeaders() });
  }

  getPaymentConfig(): Observable<LexgosignPaymentConfig> {
    return this.http
      .get<any>(`${this.apiUrl}/payment/config`, { headers: this.authHeaders() })
      .pipe(
        map((resp) => {
          // Mismo patrón que el resto del fichero: el backend responde HTTP 200
          // con `{success:false}` cuando falla, así que sin esta comprobación un
          // fallo se convertía en una config falsa — y el componente lo leía como
          // "modo demo" y saltaba la confirmación del pago.
          const action = this.unwrapAction(resp);
          if (action?.success === false) {
            throw { message: action.message || 'No se pudo leer la configuración de pagos' };
          }
          return (action?.result ?? action) as LexgosignPaymentConfig;
        })
      );
  }

  getPaymentQuote(documentCount = 1, couponCode?: string): Observable<LexgosignPaymentQuote> {
    return this.http
      .post<any>(
        `${this.apiUrl}/payment/quote`,
        { documentCount, couponCode: couponCode || '' },
        { headers: this.authHeaders() }
      )
      .pipe(
        map((resp) => {
          const action = this.unwrapAction(resp);
          if (action?.success === false) {
            throw { message: action.message || 'No se pudo calcular el costo' };
          }
          return (action?.result ?? action) as LexgosignPaymentQuote;
        })
      );
  }

  createPaymentIntent(documentCount = 1, couponCode?: string): Observable<LexgosignPaymentIntentResult> {
    return this.http
      .post<any>(
        `${this.apiUrl}/payment/intent`,
        { documentCount, couponCode: couponCode || '' },
        { headers: this.authHeaders() }
      )
            .pipe(
        map((resp) => {
          const action = this.unwrapAction(resp);
          if (action?.success === false) {
            throw { message: action.message || 'No se pudo iniciar el pago' };
          }
          return (action?.result ?? action) as LexgosignPaymentIntentResult;
        })
      );
  }

  remind(envelopePk: number | string): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/envelope/${envelopePk}/remind`, {}, { headers: this.authHeaders() })
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  void(envelopePk: number | string): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/envelope/${envelopePk}/void`, {}, { headers: this.authHeaders() })
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  evidence(envelopePk: number | string): Observable<any> {
    return this.http
      .get<any>(`${this.apiUrl}/envelope/${envelopePk}/evidence`, { headers: this.authHeaders() })
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  progress(envelopePk: number | string): Observable<LexgosignEnvelope> {
    return this.http
      .get<any>(`${this.apiUrl}/envelope/${envelopePk}/progress`, { headers: this.authHeaders() })
      .pipe(
        map((resp) => {
          const action = this.unwrapAction(resp);
          return (action?.result ?? action) as LexgosignEnvelope;
        })
      );
  }

  getSettings(scope?: string): Observable<LexgosignSettings> {
    const params: any = {};
    if (scope) params.scope = scope;
    return this.http
      .get<any>(`${this.apiUrl}/settings`, { headers: this.authHeaders(), params })
      .pipe(
        map((resp) => {
          const action = this.unwrapAction(resp);
          if (action?.success === false) {
            throw { message: action.message || 'Error al leer settings', error: action };
          }
          return (action?.result ?? action) as LexgosignSettings;
        })
      );
  }

  updateSettings(payload: LexgosignSettings): Observable<any> {
    return this.http
      .put<any>(`${this.apiUrl}/settings`, payload, { headers: this.authHeaders() })
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  listFiles(path?: string): Observable<{
    root: string;
    path: string;
    parentPath: string | null;
    currentUserId?: number | null;
    folders: {
      name: string;
      path: string;
      createdBy?: number | null;
      createdByName?: string | null;
      canManage?: boolean;
      canEdit?: boolean;
      canDelete?: boolean;
    }[];
    files: {
      name: string;
      key: string;
      path: string | null;
      size: number;
      lastModified: string | null;
      createdAt?: string | null;
      url: string | null;
      createdBy?: number | null;
      createdByName?: string | null;
      canDelete?: boolean;
    }[];
  }> {
    const params: any = {};
    if (path) params.path = path;
    return this.http
      .get<any>(`${this.apiUrl}/files`, { headers: this.authHeaders(), params })
      .pipe(
        map((resp) => {
          const action = this.unwrapAction(resp);
          if (action?.success === false) {
            throw { message: action.message || 'Error al listar archivos' };
          }
          return (action?.result ?? action) as any;
        })
      );
  }

  createFolder(path: string, name: string): Observable<any> {
    return this.http
      .post<any>(
        `${this.apiUrl}/files/folder`,
        { path, name },
        { headers: this.authHeaders() }
      )
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  renameFolder(path: string, name: string): Observable<any> {
    return this.http
      .post<any>(
        `${this.apiUrl}/files/folder/rename`,
        { path, name },
        { headers: this.authHeaders() }
      )
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  deleteFolder(path: string): Observable<any> {
    return this.http
      .post<any>(
        `${this.apiUrl}/files/folder/delete`,
        { path },
        { headers: this.authHeaders() }
      )
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  uploadArchiveFile(path: string, file: File): Observable<any> {
    const fd = new FormData();
    fd.append('path', path || 'Contratos');
    fd.append('file', file, file.name);
    return this.http
      .post<any>(`${this.apiUrl}/files/upload`, fd, { headers: this.authHeaders() })
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  downloadAllFiles(path?: string): Observable<any> {
    const params: any = {};
    if (path) params.path = path;
    return this.http
      .get<any>(`${this.apiUrl}/files/download-all`, { headers: this.authHeaders(), params })
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  deleteFile(key: string): Observable<any> {
    return this.http
      .post<any>(
        `${this.apiUrl}/files/delete`,
        { key },
        { headers: this.authHeaders() }
      )
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  moveFile(key: string, path: string): Observable<any> {
    return this.http
      .post<any>(
        `${this.apiUrl}/files/move`,
        { key, path: path || 'Contratos' },
        { headers: this.authHeaders() }
      )
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  sendToFiles(envelopeId: number | string, path?: string | null): Observable<any> {
    return this.http
      .post<any>(
        `${this.apiUrl}/files/send`,
        { envelopeId: Number(envelopeId), path: path || null },
        { headers: this.authHeaders() }
      )
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  listAlerts(params?: { sent?: boolean; envelopeId?: number }): Observable<LexgosignAlert[]> {
    const query: any = {};
    if (params?.sent === true || params?.sent === false) query.sent = params.sent;
    if (params?.envelopeId != null) query.envelopeId = params.envelopeId;
    return this.http
      .get<any>(`${this.apiUrl}/alerts`, { headers: this.authHeaders(), params: query })
      .pipe(
        map((resp) => {
          const action = this.unwrapAction(resp);
          if (action?.success === false) {
            throw { message: action.message || 'Error al listar alertas' };
          }
          const raw = action?.result ?? action;
          return Array.isArray(raw) ? raw : [];
        })
      );
  }

  listAlertCollaborators(): Observable<{ id: number; text: string; email?: string }[]> {
    return this.http
      .get<any>(`${this.apiUrl}/alerts/collaborators`, { headers: this.authHeaders() })
      .pipe(
        map((resp) => {
          const action = this.unwrapAction(resp);
          const raw = action?.result ?? action;
          return Array.isArray(raw) ? raw : [];
        })
      );
  }

  createAlert(payload: {
    envelopeId: number;
    subject: string;
    body: string;
    alertDate: string;
    alertTime: string;
    userIds: number[];
  }): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/alerts`, payload, { headers: this.authHeaders() })
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  updateAlert(
    id: number,
    payload: {
      envelopeId: number;
      subject: string;
      body: string;
      alertDate: string;
      alertTime: string;
      userIds: number[];
    }
  ): Observable<any> {
    return this.http
      .put<any>(`${this.apiUrl}/alerts/${id}`, payload, { headers: this.authHeaders() })
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  deleteAlert(id: number): Observable<any> {
    return this.http
      .post<any>(
        `${this.apiUrl}/alerts/delete`,
        { id },
        { headers: this.authHeaders() }
      )
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  listTemplateCategories(): Observable<LexgosignTemplateCategory[]> {
    return this.http
      .get<any>(`${this.apiUrl}/template-categories`, { headers: this.authHeaders() })
      .pipe(
        map((resp) => {
          const action = this.unwrapAction(resp);
          if (action?.success === false) {
            throw { message: action.message || 'Error al listar categorías' };
          }
          const raw = action?.result ?? action;
          return Array.isArray(raw) ? raw : [];
        })
      );
  }

  createTemplateCategory(name: string, parentId?: number | null): Observable<any> {
    const payload: { name: string; parentId?: number } = { name };
    if (parentId) payload.parentId = parentId;
    return this.http
      .post<any>(
        `${this.apiUrl}/template-categories`,
        payload,
        { headers: this.authHeaders() }
      )
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  updateTemplateCategory(id: number, name: string): Observable<any> {
    return this.http
      .put<any>(
        `${this.apiUrl}/template-categories/${id}`,
        { name },
        { headers: this.authHeaders() }
      )
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  deleteTemplateCategory(id: number): Observable<any> {
    return this.http
      .post<any>(
        `${this.apiUrl}/template-categories/delete`,
        { id },
        { headers: this.authHeaders() }
      )
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  listTemplates(categoryId?: number | null): Observable<LexgosignTemplate[]> {
    const params: any = {};
    if (categoryId) params.categoryId = categoryId;
    return this.http
      .get<any>(`${this.apiUrl}/templates`, { headers: this.authHeaders(), params })
      .pipe(
        map((resp) => {
          const action = this.unwrapAction(resp);
          if (action?.success === false) {
            throw { message: action.message || 'Error al listar plantillas' };
          }
          const raw = action?.result ?? action;
          return Array.isArray(raw) ? raw : [];
        })
      );
  }

  getTemplate(id: number): Observable<LexgosignTemplate> {
    return this.http
      .get<any>(`${this.apiUrl}/templates/${id}`, { headers: this.authHeaders() })
      .pipe(
        map((resp) => {
          const action = this.unwrapAction(resp);
          if (action?.success === false) {
            throw { message: action.message || 'No se pudo cargar la plantilla' };
          }
          return action?.result ?? action;
        })
      );
  }

  analyzeTemplateFile(file: File): Observable<any> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    return this.http
      .post<any>(`${this.apiUrl}/templates/analyze`, fd, { headers: this.authHeaders() })
      .pipe(
        map((resp) => {
          const action = this.unwrapAction(resp);
          const inner = this.pickAnalyzePayload(action);
          return {
            success: action?.success !== false,
            message: action?.message,
            result: inner
          };
        })
      );
  }

  private pickAnalyzePayload(action: any) {
    const candidates = [action, action?.result, action?.result?.result];
    for (const candidate of candidates) {
      if (candidate && Array.isArray(candidate.fields)) return candidate;
    }
    return action?.result || action;
  }

  createTemplate(payload: {
    name: string;
    categoryId: number;
    fields: LexgosignTemplateField[];
    file: File;
  }): Observable<any> {
    const fd = new FormData();
    fd.append('name', payload.name);
    fd.append('categoryId', String(payload.categoryId));
    fd.append('fields', JSON.stringify(payload.fields || []));
    fd.append('file', payload.file, payload.file.name);
    return this.http
      .post<any>(`${this.apiUrl}/templates`, fd, { headers: this.authHeaders() })
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  previewTemplate(payload: {
    file?: File | null;
    templateId?: number | null;
    fields?: LexgosignTemplateField[];
  }): Observable<any> {
    const fd = new FormData();
    if (payload.file) {
      fd.append('file', payload.file, payload.file.name);
    }
    if (payload.templateId) {
      fd.append('templateId', String(payload.templateId));
    }
    fd.append('fields', JSON.stringify(payload.fields || []));
    return this.http
      .post<any>(`${this.apiUrl}/templates/preview`, fd, { headers: this.authHeaders() })
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  updateTemplate(payload: {
    id: number;
    name: string;
    categoryId: number;
    fields: LexgosignTemplateField[];
    file?: File | null;
  }): Observable<any> {
    const fd = new FormData();
    fd.append('id', String(payload.id));
    fd.append('name', payload.name);
    fd.append('categoryId', String(payload.categoryId));
    fd.append('fields', JSON.stringify(payload.fields || []));
    if (payload.file) {
      fd.append('file', payload.file, payload.file.name);
    }
    return this.http
      .post<any>(`${this.apiUrl}/templates/update`, fd, { headers: this.authHeaders() })
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  deleteTemplate(id: number): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}/templates/delete`, { id }, { headers: this.authHeaders() })
      .pipe(map((resp) => this.unwrapAction(resp)));
  }

  generateFromTemplate(
    templateId: number,
    values: Record<string, string>
  ): Observable<any> {
    return this.http
      .post<any>(
        `${this.apiUrl}/templates/generate`,
        { templateId, values },
        { headers: this.authHeaders() }
      )
      .pipe(map((resp) => this.unwrapAction(resp)));
  }
}
